import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/components/ui/use-toast'
import { useAuth } from '@/context/AuthContext'
import { apiClient } from '@/lib/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	Loader2,
	Upload,
	Save,
	MessageSquare,
	Sparkles,
	CheckCircle2,
	AlertCircle,
	User,
	Briefcase,
} from 'lucide-react'
import { AgentStatusIndicator } from '@/components/agent-status/AgentStatusIndicator'
import { supabase } from '@/integrations/supabase/client'
import { useTranslation } from '@/hooks/useTranslation'

interface AgentProfile {
	id?: string
	user_id: string
	program_id?: string
	full_name: string
	job_title?: string
	avatar_url?: string
	is_active?: boolean
	display_in_widget?: boolean
}

interface LiveChatSettingsProps {
	defaultTab?: string
	programId?: string | null
	showTeamManagement?: boolean
}

export default function LiveChatSettings({
	defaultTab = 'agent',
	programId,
	showTeamManagement = false,
}: LiveChatSettingsProps) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const queryClient = useQueryClient()

	const [profile, setProfile] = useState<AgentProfile>({
		user_id: user?.id || '',
		program_id: programId || '',
		full_name:
			user?.user_metadata?.full_name || user?.email?.split('@')[0] || '',
		job_title: '',
		avatar_url: '',
		is_active: true,
		display_in_widget: true,
	})

	const [uploadingAvatar, setUploadingAvatar] = useState(false)
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

	// Fetch existing profile
	const { data: existingProfile, isLoading, error } = useQuery({
		queryKey: ['agent-profile', user?.id, programId],
		queryFn: async () => {
			const url = `/api/agent-profile?program_id=${programId}`
			const response = await apiClient(url)
			return response as {
				profile: AgentProfile | null
				working_hours: WorkingHours[]
				program_info?: {
					id: string
					name: string
					team_id: string
				}
				template?: AgentProfile
			}
		},
		enabled: !!user && !!programId,
	})

	useEffect(() => {
		if (existingProfile) {
			if (existingProfile.profile) {
				// User has an existing profile in this program
				const profileData = {
					...existingProfile.profile,
					user_id: existingProfile.profile.user_id || user?.id || '',
					program_id: programId || '',
				}
				setProfile(profileData)
			} else if (existingProfile.template) {
				// No profile exists yet, use template for initial values
				const templateData = {
					...existingProfile.template,
					full_name:
						existingProfile.template.full_name ||
						user?.user_metadata?.full_name ||
						user?.email?.split('@')[0] ||
						'',
				}
				setProfile(templateData)
			} else {
				// Fallback to basic defaults
				setProfile((prev) => ({
					...prev,
					user_id: user?.id || '',
					program_id: programId || '',
					full_name:
						prev.full_name ||
						user?.user_metadata?.full_name ||
						user?.email?.split('@')[0] ||
						'',
				}))
			}
			setHasUnsavedChanges(false)
		}
	}, [existingProfile, user, programId])

	// Save profile mutation
	const saveProfileMutation = useMutation({
		mutationFn: async (data: { profile: AgentProfile }) => {
			const url = `/api/agent-profile?program_id=${programId}`

			const response = await apiClient(url, {
				method: 'PUT',
				body: JSON.stringify({
					profileData: { ...data.profile },
				}),
			})
			return response
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['agent-profile', user?.id, programId],
			})
			queryClient.invalidateQueries({ queryKey: ['agent-status'] })
			queryClient.invalidateQueries({ queryKey: ['team-agents'] })
			queryClient.invalidateQueries({ queryKey: ['program-agents', programId] })
			toast({
				title: t('liveChatSettings.success'),
				description: t('liveChatSettings.liveChatSettingsUpdated'),
			})
			setHasUnsavedChanges(false)
		},
		onError: () => {
			toast({
				title: t('liveChatSettings.error'),
				description: t('liveChatSettings.failedToUpdateSettings'),
				variant: 'destructive',
			})
		},
	})

	const handleAvatarUpload = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = event.target.files?.[0]

		if (!file || !user || !programId) {
			if (!programId) {
				toast({
					title: t('liveChatSettings.error'),
					description: t('liveChatSettings.programIdRequired'),
					variant: 'destructive',
				})
			}
			return
		}

		setUploadingAvatar(true)
		try {
			const fileExt = file.name.split('.').pop()
			const fileName = `${user.id}-${Date.now()}.${fileExt}`
			const filePath = `agent-avatars/programs/${programId}/${fileName}`

			const { data: uploadData, error: uploadError } = await supabase.storage
				.from('avatars')
				.upload(filePath, file, { upsert: true })

			if (uploadError) throw uploadError

			const {
				data: { publicUrl },
			} = supabase.storage.from('avatars').getPublicUrl(filePath)

			setProfile((prev) => ({ ...prev, avatar_url: publicUrl }))
			setHasUnsavedChanges(true)
			toast({
				title: t('liveChatSettings.success'),
				description: t('liveChatSettings.avatarUploadedSuccessfully'),
			})
		} catch (error) {
			toast({
				title: t('liveChatSettings.error'),
				description: t('liveChatSettings.failedToUploadAvatar', {
					error: error instanceof Error ? error.message : t('liveChatSettings.unknownError')
				}),
				variant: 'destructive',
			})
		} finally {
			setUploadingAvatar(false)
		}
	}

	const handleSave = () => {
		if (!profile.full_name.trim()) {
			toast({
				title: t('liveChatSettings.error'),
				description: t('liveChatSettings.pleaseEnterFullName'),
				variant: 'destructive',
			})
			return
		}

		saveProfileMutation.mutate({
			profile,
		})
	}

	// Handle no program selected
	if (!programId) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-4">
				<MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
				<h2 className="text-lg font-semibold text-foreground mb-2">
					{t('liveChatSettings.noProgramSelected')}
				</h2>
				<p className="text-sm text-muted-foreground text-center max-w-sm">
					{t('liveChatSettings.pleaseSelectProgram')}
				</p>
			</div>
		)
	}

	// Handle loading state
	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-4">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
				<p className="text-sm text-muted-foreground">{t('liveChatSettings.loadingYourProfile')}</p>
			</div>
		)
	}

	// Handle error state
	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-4">
				<AlertCircle className="h-12 w-12 text-destructive/50 mb-4" />
				<h2 className="text-lg font-semibold text-foreground mb-2">
					{t('liveChatSettings.somethingWentWrong')}
				</h2>
				<p className="text-sm text-muted-foreground text-center max-w-sm mb-4">
					{t('liveChatSettings.couldntLoadProfileSettings')}
				</p>
				<Button
					variant="outline"
					onClick={() => queryClient.invalidateQueries({
						queryKey: ['agent-profile', user?.id, programId]
					})}>
					{t('liveChatSettings.tryAgain')}
				</Button>
			</div>
		)
	}

	// Check if this is a new profile
	const isNewProfile = !existingProfile?.profile?.id

	return (
		<div className="mx-auto max-w-4xl space-y-8 px-4 py-6">
			{/* Header Section */}
			<div className="space-y-1">
				<h1 className="text-2xl font-semibold text-foreground">
					{t('liveChatSettings.agentProfile')}
				</h1>
				<p className="text-sm text-muted-foreground">
					{t('liveChatSettings.howYouAppearToVisitors')}
				</p>
			</div>

			{/* New Profile Welcome */}
			{isNewProfile && (
				<div className="rounded-xl border-2 border-dashed border-primary/20 bg-primary/5 p-6 text-center">
					<Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
					<h2 className="text-lg font-semibold text-foreground mb-1">
						{t('liveChatSettings.welcomeToLiveChat')}
					</h2>
					<p className="text-sm text-muted-foreground max-w-md mx-auto">
						{t('liveChatSettings.addYourPhotoAndDetails')}
					</p>
				</div>
			)}

			{/* Profile Card - Hero Section */}
			<div className="relative rounded-2xl border bg-gradient-to-br from-white to-gray-50/50 p-6 shadow-sm">
				<div className="relative flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-6">
					{/* Avatar Section */}
					<div className="relative">
						<Avatar className="h-32 w-32 ring-4 ring-white shadow-xl">
							{profile.avatar_url && (
								<AvatarImage
									src={profile.avatar_url}
									alt={profile.full_name}
									className="object-cover"
								/>
							)}
							<AvatarFallback className="text-2xl font-medium bg-primary/10 text-primary">
								{profile.full_name
									.split(' ')
									.filter((n) => n)
									.map((n) => n[0])
									.join('')
									.toUpperCase() || 'U'}
							</AvatarFallback>
						</Avatar>
						<label htmlFor="avatar-upload">
							<Button
								variant="secondary"
								size="sm"
								className="absolute -bottom-2 left-1/2 -translate-x-1/2 shadow-md hover:shadow-lg transition-all duration-200"
								disabled={uploadingAvatar}
								asChild>
								<span className="cursor-pointer">
									{uploadingAvatar ? (
										<Loader2 className="h-3 w-3 animate-spin mr-1.5" />
									) : (
										<Upload className="h-3 w-3 mr-1.5" />
									)}
									{t('liveChatSettings.uploadPhoto')}
								</span>
							</Button>
						</label>
						<input
							id="avatar-upload"
							type="file"
							accept="image/*"
							className="hidden"
							onChange={handleAvatarUpload}
							disabled={uploadingAvatar}
						/>
					</div>

					{/* Profile Info */}
					<div className="flex-1 text-center sm:text-left space-y-3">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								{profile.full_name || t('liveChatSettings.yourName')}
							</h2>
							<p className="text-sm text-muted-foreground">
								{profile.job_title || t('liveChatSettings.yourRole')}
							</p>
						</div>
						<div className="flex items-center gap-2 justify-center sm:justify-start relative z-10">
							<AgentStatusIndicator programId={programId} />
						</div>
						<div className="flex items-center gap-1.5 text-xs text-muted-foreground justify-center sm:justify-start">
							<CheckCircle2 className="h-3 w-3 text-green-500" />
							<span>{t('liveChatSettings.visibleToVisitorsWhenOnline')}</span>
						</div>
					</div>
				</div>
			</div>

			{/* Form Section */}
			<div className="space-y-6">
				{/* Basic Information */}
				<div className="rounded-xl border bg-card p-6 space-y-6">
					<div>
						<h3 className="text-base font-medium text-foreground flex items-center gap-2">
							<User className="h-4 w-4 text-muted-foreground" />
							{t('liveChatSettings.profileInformation')}
						</h3>
						<p className="text-sm text-muted-foreground mt-1">
							{t('liveChatSettings.visitorsMoreLikelyToStartChat')}
						</p>
					</div>

					<div className="grid gap-6 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="full-name" className="text-sm font-medium">
								{t('liveChatSettings.fullName')}
								<span className="text-destructive ml-1">*</span>
							</Label>
							<div className="relative">
								<Input
									id="full-name"
									value={profile.full_name}
									onChange={(e) => {
										setProfile({ ...profile, full_name: e.target.value })
										setHasUnsavedChanges(true)
									}}
									placeholder={t('liveChatSettings.yourFullName')}
									className="pr-8"
								/>
								{profile.full_name && (
									<CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
								)}
							</div>
						</div>
						<div className="space-y-2">
							<Label htmlFor="job-title" className="text-sm font-medium">
								<Briefcase className="h-3 w-3 inline mr-1.5 text-muted-foreground" />
								{t('liveChatSettings.jobTitle')}
							</Label>
							<Input
								id="job-title"
								value={profile.job_title || ''}
								onChange={(e) => {
									setProfile({ ...profile, job_title: e.target.value })
									setHasUnsavedChanges(true)
								}}
								placeholder={t('liveChatSettings.jobTitlePlaceholder')}
							/>
						</div>
					</div>
				</div>

				{/* Availability Settings */}
				<div className="rounded-xl border bg-card p-6 space-y-4">
					<div>
						<h3 className="text-base font-medium text-foreground flex items-center gap-2">
							<Sparkles className="h-4 w-4 text-muted-foreground" />
							{t('liveChatSettings.availabilitySettings')}
						</h3>
					</div>

					<div className="space-y-4">
						<div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-background/50 hover:bg-accent/5 transition-colors duration-200">
							<div className="space-y-1">
								<Label htmlFor="is-active" className="text-sm font-medium cursor-pointer">
									{t('liveChatSettings.activeForChatSupport')}
								</Label>
								<p className="text-xs text-muted-foreground">
									{t('liveChatSettings.automaticallyHidesYouWhenUnavailable')}
								</p>
							</div>
							<Switch
								id="is-active"
								checked={profile.is_active}
								onCheckedChange={(checked) => {
									setProfile({ ...profile, is_active: checked })
									setHasUnsavedChanges(true)
								}}
								className="data-[state=checked]:bg-primary"
							/>
						</div>

						<div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-background/50 hover:bg-accent/5 transition-colors duration-200">
							<div className="space-y-1">
								<Label htmlFor="display-widget" className="text-sm font-medium cursor-pointer">
									{t('liveChatSettings.showProfileInWidget')}
								</Label>
								<p className="text-xs text-muted-foreground">
									{t('liveChatSettings.showsYourPhotoAndName')}
								</p>
							</div>
							<Switch
								id="display-widget"
								checked={profile.display_in_widget}
								onCheckedChange={(checked) => {
									setProfile({ ...profile, display_in_widget: checked })
									setHasUnsavedChanges(true)
								}}
								className="data-[state=checked]:bg-primary"
							/>
						</div>
					</div>
				</div>

				{/* Widget Preview */}
				<div className="rounded-xl border bg-gradient-to-br from-gray-50 to-white p-6 space-y-4">
					<div>
						<h3 className="text-base font-medium text-foreground flex items-center gap-2">
							<MessageSquare className="h-4 w-4 text-muted-foreground" />
							{t('liveChatSettings.widgetPreview')}
						</h3>
						<p className="text-sm text-muted-foreground mt-1">
							{t('liveChatSettings.previewUpdatesAsYouType')}
						</p>
					</div>

					<div className="flex justify-center">
						<div className="w-full max-w-sm">
							<div className="rounded-xl border bg-white shadow-lg overflow-hidden">
								<div className="bg-primary px-4 py-3">
									<p className="text-sm font-medium text-primary-foreground">
										{t('liveChatSettings.chatWith', { name: profile.full_name || t('liveChatSettings.agent') })}
									</p>
								</div>
								<div className="p-4 space-y-3">
									<div className="flex items-start gap-3">
										<Avatar className="h-8 w-8 ring-2 ring-white shadow">
											{profile.avatar_url && (
												<AvatarImage
													src={profile.avatar_url}
													alt={profile.full_name}
												/>
											)}
											<AvatarFallback className="text-xs bg-primary/10 text-primary">
												{profile.full_name
													.split(' ')
													.filter((n) => n)
													.map((n) => n[0])
													.join('')
													.toUpperCase() || 'U'}
											</AvatarFallback>
										</Avatar>
										<div className="flex-1 space-y-1">
											<div className="flex items-baseline gap-2">
												<p className="text-sm font-medium">{profile.full_name || t('liveChatSettings.agent')}</p>
												{profile.job_title && (
													<span className="text-xs text-muted-foreground">
														• {profile.job_title}
													</span>
												)}
											</div>
											<div className="bg-gray-100 rounded-lg px-3 py-2">
												<p className="text-sm text-gray-700">
													{t('liveChatSettings.defaultGreetingMessage')}
												</p>
											</div>
										</div>
									</div>
									<div className="pt-2">
										<div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-gray-50/50">
											<p className="text-sm text-muted-foreground">{t('liveChatSettings.typeAMessage')}</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Action Bar */}
			<div className="sticky bottom-0 -mx-4 bg-gradient-to-t from-background via-background to-transparent px-4 py-4">
				<div className="flex items-center justify-between gap-4 rounded-xl border bg-card/95 backdrop-blur p-4 shadow-lg">
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						{hasUnsavedChanges && (
							<>
								<AlertCircle className="h-4 w-4 text-amber-500" />
								<span>{t('liveChatSettings.youHaveUnsavedChanges')}</span>
							</>
						)}
					</div>
					<Button
						onClick={handleSave}
						disabled={saveProfileMutation.isPending || !hasUnsavedChanges}
						className="transition-all duration-200">
						{saveProfileMutation.isPending ? (
							<>
								<Loader2 className="h-4 w-4 animate-spin mr-2" />
								{t('liveChatSettings.saving')}
							</>
						) : (
							<>
								<Save className="h-4 w-4 mr-2" />
								{t('liveChatSettings.saveChanges')}
							</>
						)}
					</Button>
				</div>
			</div>
		</div>
	)
}